/**
 * Every date, time, number and price the UI renders goes through here.
 *
 * Before this existed, 186 call sites passed a hardcoded locale tag —
 * 162 of them 'de-AT' — and the "dynamic" ones were an
 * `i18n.language === 'sr' ? 'sr-Latn' : … : 'de-AT'` ternary copy-pasted into
 * eight files with no branch for a fourth language. Adding Turkish would have
 * formatted Turkish dates as Austrian German everywhere.
 *
 * Three things are decided centrally here, because getting any of them wrong is
 * invisible until a client complains:
 *
 * 1. THE LOCALE TAG comes from the registry (@4m/shared), never from the raw
 *    i18next code. That is what pins German to de-AT and forces the Gregorian
 *    calendar and Latin digits on locales whose default is neither — without it
 *    a Persian-locale user reads an appointment booked for 22.08.2026 as
 *    ۳۱ مرداد ۱۴۰۵.
 *
 * 2. THE TIME ZONE is Europe/Vienna, always. Not one of the previous call sites
 *    pinned it, so every time in the app silently rendered in whatever zone the
 *    viewer's machine was set to. A makler abroad saw a different appointment
 *    time than the client was told.
 *
 * 3. MISSING INPUT renders '—', not 'Invalid Date' or '€ NaN'. Timestamps arrive
 *    from Drizzle as ISO strings while the *Row types declare number — the
 *    project's most repeated bug — so every entry point normalises through
 *    toMs() rather than trusting the declared type.
 */

import { DEFAULT_LOCALE, intlTag, isSupportedLocale } from '@4m/shared';
import { toMs } from './appointmentDate';

const TIME_ZONE = 'Europe/Vienna';
const MISSING = '—';

/** Mirrors the active UI language. Updated from i18n's languageChanged event so
 *  this module never has to import the i18next instance — which would make it
 *  untestable and create a cycle. */
let activeLocale: string = DEFAULT_LOCALE;

export function setFormattingLocale(code: string | undefined | null): void {
  if (isSupportedLocale(code)) activeLocale = code as string;
}

export function currentFormattingLocale(): string {
  return activeLocale;
}

/**
 * ECMA-402 treats the two option families as mutually exclusive: `dateStyle` /
 * `timeStyle` describe a whole stamp, `day` / `hour` / `weekday` describe its
 * parts, and asking for both makes the Intl.DateTimeFormat CONSTRUCTOR throw
 * `TypeError: Invalid option : option` before it formats anything.
 *
 * These helpers exist to inject house defaults (Austrian dd.MM.yyyy, Vienna
 * time), which means a caller who asks for a style would otherwise get those
 * defaults merged in underneath and crash. It happened: eleven call sites
 * across seven screens — /appointments, the appointment dialog, ⌘K search,
 * the audit log, voice calls — went down with a blank error page.
 *
 * So the caller decides the family. Ask for a style and the component defaults
 * step aside; ask for nothing and you get the house defaults. A stray
 * component option arriving ALONGSIDE a style is dropped rather than passed
 * on, because rendering a slightly different date beats taking the screen out.
 */
const COMPONENT_KEYS = [
  'weekday', 'era', 'year', 'month', 'day', 'dayPeriod',
  'hour', 'minute', 'second', 'fractionalSecondDigits',
] as const;

function withStyleOrParts(
  opts: Intl.DateTimeFormatOptions | undefined,
  parts: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
  const styled = opts?.dateStyle !== undefined || opts?.timeStyle !== undefined;
  if (!styled) return { ...parts, timeZone: TIME_ZONE, ...opts };
  const clean: Record<string, unknown> = { ...opts };
  for (const key of COMPONENT_KEYS) delete clean[key];
  // timeZone is legal next to a style, and dropping it would undo the whole
  // reason this module exists.
  return { ...(clean as Intl.DateTimeFormatOptions), timeZone: TIME_ZONE };
}

type Value = number | string | Date | null | undefined;

function resolve(locale?: string): string {
  return intlTag(locale ?? activeLocale);
}

function instant(value: Value): Date | null {
  const ms = toMs(value ?? null);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

export function formatDate(
  value: Value,
  opts?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const d = instant(value);
  if (!d) return MISSING;
  return new Intl.DateTimeFormat(
    resolve(locale),
    // dd.MM.yyyy is the Austrian business convention and what the contracts
    // print. The bare toLocaleDateString('de-AT') these calls used before gave
    // "22.8.2026" at 37 sites and "22.08.2026" at 3 — inconsistent already.
    withStyleOrParts(opts, { day: '2-digit', month: '2-digit', year: 'numeric' }),
  ).format(d);
}

export function formatTime(
  value: Value,
  opts?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const d = instant(value);
  if (!d) return MISSING;
  return new Intl.DateTimeFormat(
    resolve(locale),
    withStyleOrParts(opts, { hour: '2-digit', minute: '2-digit' }),
  ).format(d);
}

export function formatDateTime(
  value: Value,
  opts?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const d = instant(value);
  if (!d) return MISSING;
  // A caller asking for dateStyle/timeStyle is describing the WHOLE stamp, so
  // one formatter produces it. Concatenating date + time here would print the
  // date, and then print it again inside the styled half.
  if (opts?.dateStyle !== undefined || opts?.timeStyle !== undefined) {
    return new Intl.DateTimeFormat(resolve(locale), withStyleOrParts(opts, {})).format(d);
  }
  return `${formatDate(d, undefined, locale)}, ${formatTime(d, opts, locale)}`;
}

export function formatNumber(
  value: number | string | null | undefined,
  opts?: Intl.NumberFormatOptions,
  locale?: string,
): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n == null || !Number.isFinite(n)) return MISSING;
  return new Intl.NumberFormat(resolve(locale), opts).format(n);
}

/**
 * A EUR amount. Symbol placement and grouping follow the locale — Austrian
 * "€ 1.234.568", French "1 234 568 €" — which is why prices must never be built
 * by concatenating a '€' onto a formatted number.
 */
export function formatMoney(
  value: number | string | null | undefined,
  opts?: Intl.NumberFormatOptions,
  locale?: string,
): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n == null || !Number.isFinite(n)) return MISSING;
  return new Intl.NumberFormat(resolve(locale), {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
    ...opts,
  }).format(n);
}

const RELATIVE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * "vor 3 Tagen" / "3 days ago", in the active locale.
 *
 * Replaces three hand-written German-only helpers that returned their strings
 * outside t() entirely, so they stayed German in every language.
 */
export function formatRelative(value: Value, locale?: string): string {
  const d = instant(value);
  if (!d) return MISSING;
  const diff = d.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(resolve(locale), { numeric: 'auto' });
  for (const [unit, ms] of RELATIVE_STEPS) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.trunc(diff / ms), unit);
  }
  return rtf.format(Math.trunc(diff / 1000), 'second');
}
